import {
  useId, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode,
} from 'react';
import {
  filterOptionCount, isPicked, scopeOf, type BoardPick,
} from '../logic/dashboard';

// TODO(witems): SwitchTab draws text only, so it cannot stack a count over each segment — see docs/witems-feedback.md.
interface BoardTilesProps {
  filters?: unknown[];
  enrolled?: number | null; // null while the first load is in flight
  selected?: Record<string, string[]>;
  onPick: (pick: BoardPick) => void;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
// The visible count sits inside the button, so its accessible name carries it too.
const named = (label: string, n: number | null) => (n === null ? label : `${label}, ${n} ${plural(n, 'patient', 'patients')}`);

interface TileProps {
  label: string;
  tip: string;
  wide?: boolean;
  children: (tipId: string, dismiss: () => void) => ReactNode;
}

// Moving between a tile's own segments is not entering or leaving it.
const crossesTile = (e: FocusEvent<HTMLDivElement>) => !e.currentTarget.contains(e.relatedTarget as Node | null);

// Hover or focus shows the tile's tooltip; it is always in the DOM so aria-describedby resolves.
// A pick or Escape dismisses it until the pointer or focus comes back: focus stays on the segment,
// and an open tooltip would cover the sections below.
function Tile({
  label, tip, wide, children,
}: TileProps) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const tipId = useId();
  return (
    <div
      className={`board-tile${wide ? ' is-wide' : ''}`}
      onMouseEnter={() => { setHover(true); setDismissed(false); }}
      onMouseLeave={() => setHover(false)}
      onFocus={(e) => { if (crossesTile(e)) { setFocus(true); setDismissed(false); } }}
      onBlur={(e) => { if (crossesTile(e)) setFocus(false); }}
    >
      <span className="board-tile__label">{label}</span>
      <div className="board-tile__body">{children(tipId, () => setDismissed(true))}</div>
      <div id={tipId} role="tooltip" hidden={!(hover || focus) || dismissed} className="kpi-tooltip">{tip}</div>
    </div>
  );
}

// `dot` marks an alert level with its colour; the count itself stays in ink.
function Count({
  value, word, tone, dot,
}: { value: number | null; word: string; tone?: string; dot?: 'bad' | 'high' }) {
  return (
    <>
      {dot && <span className={`alert-dot is-${dot}`} aria-hidden="true" />}
      <span className={`board-tile__count${tone ? ` is-${tone}` : ''}`}>{value ?? '—'}</span>
      {' '}
      <span className="board-tile__word">{word}</span>
    </>
  );
}

const ARROW_STEP: Record<string, number> = {
  ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1,
};
const last = (n: number) => n - 1;

const SCOPES: { value: string | null; word: string }[] = [
  { value: null, word: 'enrolled' },
  { value: 'aligned', word: 'aligned' },
  { value: 'withdrawn', word: 'withdrawn' },
];

interface PatientsTileProps {
  filters?: unknown[];
  enrolled: number | null;
  selected: Record<string, string[]>;
  onPick: (p: BoardPick) => void;
  loaded: boolean;
}

function PatientsTile({
  filters, enrolled, selected, onPick, loaded,
}: PatientsTileProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const scope = scopeOf(selected);
  const countOf = (value: string | null) => {
    if (!loaded) return null;
    return value === null ? enrolled : filterOptionCount(filters, 'lifecycle', value);
  };
  const checkedIndex = SCOPES.findIndex((s) => s.value === scope);
  const choose = (next: number) => {
    refs.current[next]?.focus();
    const target = SCOPES[next];
    if (target && next !== checkedIndex) onPick({ key: 'lifecycle', value: target.value });
  };
  // Arrow keys, Home and End move the choice, as in any radio group.
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number, dismiss: () => void) => {
    if (e.key === 'Escape') { dismiss(); return; }
    const step = ARROW_STEP[e.key];
    let next: number | null = step ? (i + step + SCOPES.length) % SCOPES.length : null;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = last(SCOPES.length);
    if (next === null) return;
    e.preventDefault();
    choose(next);
  };
  // A second click on aligned or withdrawn returns to enrolled, and focus follows the choice.
  const onClick = (i: number, dismiss: () => void) => {
    dismiss();
    if (i === checkedIndex && i !== 0) {
      refs.current[0]?.focus();
      onPick({ key: 'lifecycle', value: null });
      return;
    }
    onPick({ key: 'lifecycle', value: SCOPES[i]?.value ?? null });
  };
  return (
    <Tile
      label="Patients"
      wide
      tip={'Enrolled: every patient in care, the default list. Aligned: aligned with CMS. Withdrawn: left the program.\n\nThe choice scopes the list and the journey counts, and stays while you click the other tiles.'}
    >
      {(tipId, dismiss) => (
        <div role="radiogroup" aria-label="Patients" aria-describedby={tipId} className="board-tile__segs">
          {SCOPES.map((s, i) => {
            const checked = i === checkedIndex;
            return (
              <span key={s.word} className="board-tile__seg-wrap">
                {i > 0 && <span className="board-tile__rule" aria-hidden="true" />}
                <button
                  ref={(el) => { refs.current[i] = el; }}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked || (checkedIndex < 0 && i === 0) ? 0 : -1}
                  className={`board-seg${checked ? ' is-selected' : ''}`}
                  disabled={!loaded}
                  onClick={() => onClick(i, dismiss)}
                  onKeyDown={(e) => onKeyDown(e, i, dismiss)}
                >
                  <Count value={countOf(s.value)} word={s.word} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </Tile>
  );
}

interface ToggleProps {
  pickKey: 'alert' | 'engagement';
  value: string;
  label: string;
  tipId: string;
  selected: Record<string, string[]>;
  disabled: boolean;
  fill?: boolean;
  close: () => void;
  onPick: (pick: BoardPick) => void;
  children: ReactNode;
}

function Toggle({
  pickKey, value, label, tipId, selected, disabled, fill, close, onPick, children,
}: ToggleProps) {
  const on = isPicked(selected, pickKey, value);
  return (
    <button
      type="button"
      className={`board-seg${fill ? ' is-fill' : ''}${on ? ' is-selected' : ''}`}
      aria-pressed={on}
      aria-label={label}
      aria-describedby={tipId}
      disabled={disabled}
      onClick={() => { close(); onPick({ key: pickKey, value }); }}
      onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
    >
      {children}
    </button>
  );
}

export default function BoardTiles({
  filters = [], enrolled = null, selected = {}, onPick,
}: BoardTilesProps) {
  const loaded = enrolled !== null;
  const count = (key: string, value: string) => (loaded ? filterOptionCount(filters, key, value) : null);
  const red = count('alert', 'red');
  // apiv2 calls it yellow; the board says orange, the alert colour.
  const orange = count('alert', 'yellow');
  const unread = count('engagement', 'unread');
  const quiet = count('engagement', 'quiet_7d');
  const shared = { selected, disabled: !loaded, onPick };

  return (
    <div className="board-tiles">
      <PatientsTile filters={filters} enrolled={enrolled} selected={selected} onPick={onPick} loaded={loaded} />
      <Tile
        label="Alerts"
        tip={`Patients with an open alert task.\n\n• Red (urgent): ${red ?? '—'} — e.g. a BP crisis\n• Orange (high): ${orange ?? '—'} — e.g. BP stage 2, missed readings\n\nClick a number to list those patients. Click again to clear.`}
      >
        {(tipId, close) => (
          <div className="board-tile__segs">
            <Toggle pickKey="alert" value="red" label={named('Filter roster to red alerts', red)} tipId={tipId} close={close} {...shared}>
              <Count value={red} word="red" dot="bad" />
            </Toggle>
            <span className="board-tile__rule" aria-hidden="true" />
            <Toggle pickKey="alert" value="yellow" label={named('Filter roster to orange alerts', orange)} tipId={tipId} close={close} {...shared}>
              <Count value={orange} word="orange" dot="high" />
            </Toggle>
          </div>
        )}
      </Tile>
      <Tile
        label="Unread messages"
        tip={'Patients with a message the care team has not read yet, from the app or the clinic line.\n\nClick to list them. Click again to clear.'}
      >
        {(tipId, close) => (
          <Toggle pickKey="engagement" value="unread" label={named('Filter roster: Unread messages', unread)} tipId={tipId} close={close} fill {...shared}>
            <Count value={unread} word={plural(unread ?? 0, 'patient waiting', 'patients waiting')} tone={unread ? 'info' : undefined} />
          </Toggle>
        )}
      </Tile>
      <Tile
        label="No reading 7 days"
        tip={'Aligned patients with no BP or weight reading in the last 7 days. A patient can also be in Unread messages or Stuck in onboarding.\n\nClick to list them. Click again to clear.'}
      >
        {(tipId, close) => (
          <Toggle pickKey="engagement" value="quiet_7d" label={named('Filter roster: No reading 7 days', quiet)} tipId={tipId} close={close} fill {...shared}>
            <Count value={quiet} word={plural(quiet ?? 0, 'patient', 'patients')} />
          </Toggle>
        )}
      </Tile>
    </div>
  );
}
